import { noteDispatchText } from './dispatchText'
import type { NoteAttachment } from '../transport/api'

/**
 * The text a set of ATTACHED files puts into a terminal agent's input.
 *
 * Deliberately delegates to `noteDispatchText` rather than formatting tags here. The
 * two features arrive at the same place — an agent's own input box, holding
 * `<note-file:PATH>` lines — and the rule that makes them correct is not the tag
 * format but the one underneath it: **the path is absolute and only exists on this
 * edge**, so an agent running on another host must be given the words without them.
 *
 * A second copy of that rule is exactly how the two would drift, and the direction it
 * drifts is the bad one: nothing fails, the dispatch looks like it worked, the agent
 * says it cannot find a file, and neither end can see why. Notes learned that once
 * already; attachment sharing does not need to learn it again.
 */
export function attachDispatchText(
  text: string,
  files: NoteAttachment[],
  /** Host of the agent this is aimed at. null = this machine. */
  host: string | null,
): { text: string; withheld: number } {
  return noteDispatchText({ text, attachments: files }, host)
}

/**
 * What to tell the user when paths were withheld.
 *
 * Withholding is correct and still needs saying: from the composer, a file that was
 * uploaded but not mentioned is indistinguishable from one that was sent. The count
 * is the part worth stating — "some" would leave them counting chips to work out
 * whether the one that mattered got through.
 */
export function withheldNotice(withheld: number, host: string): string {
  const n = withheld === 1 ? 'file' : `${withheld} files`
  return `${n} uploaded here but not sent: ${host} cannot read a path on this machine.`
}
